import React from 'react';

// Borderless icon action buttons (Edit / Delete / Copy) shared across the
// Cognitive Flows dashboard. Extracted from the Data Sources page so the
// Context page panels (KnowledgePanel, PermanentStorePanel) and Data Sources
// render the exact same pencil / trash / copy icons from one source of truth.
//
// Icon-only, but each carries a title + aria-label so it stays keyboard- and
// screen-reader-accessible and findable by role/name (e.g. in tests).

const baseStyle = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '6px',
  lineHeight: 1,
  color: '#9ca3af',
  transition: 'color 0.15s, background 0.15s',
};

const REST_COLOR = '#9ca3af';

// `disabled` (opt-in, off by default so every existing caller renders exactly
// as before) keeps the icon in place but greys it and blocks the click — the
// slot stays where the eye expects it rather than the row reflowing.
export function IconActionButton({ onClick, title, ariaLabel, hoverColor, hoverBg, restColor = REST_COLOR, restBg = 'none', pressed, disabled = false, style, children }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={ariaLabel || title}
      aria-pressed={pressed}
      disabled={disabled}
      onClick={onClick}
      style={{ ...baseStyle, color: restColor, background: restBg, ...(disabled ? { cursor: 'not-allowed', opacity: 0.45 } : null), ...style }}
      onMouseEnter={(e) => { if (disabled) return; e.currentTarget.style.color = hoverColor; e.currentTarget.style.background = hoverBg; }}
      onMouseLeave={(e) => { e.currentTarget.style.color = restColor; e.currentTarget.style.background = restBg; }}
    >
      {children}
    </button>
  );
}

export function EditIconButton({ onClick, title = 'Edit', ariaLabel, size = 22, style }) {
  return (
    <IconActionButton onClick={onClick} title={title} ariaLabel={ariaLabel} hoverColor="#6b7280" hoverBg="#f3f4f6" style={style}>
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clipPath="url(#clip_edit_icon)">
          <path d="M19.1667 13.3333H15.6667C14.2666 13.3333 13.5666 13.3333 13.0318 13.6058C12.5614 13.8455 12.1789 14.2279 11.9392 14.6983C11.6667 15.2331 11.6667 15.9332 11.6667 17.3333V24.3333C11.6667 25.7335 11.6667 26.4335 11.9392 26.9683C12.1789 27.4387 12.5614 27.8212 13.0318 28.0608C13.5666 28.3333 14.2666 28.3333 15.6667 28.3333H22.6667C24.0669 28.3333 24.7669 28.3333 25.3017 28.0608C25.7721 27.8212 26.1546 27.4387 26.3943 26.9683C26.6667 26.4335 26.6667 25.7335 26.6667 24.3333V20.8333M16.6667 23.3333H18.0622C18.4698 23.3333 18.6737 23.3333 18.8655 23.2873C19.0355 23.2464 19.1981 23.1791 19.3472 23.0877C19.5154 22.9847 19.6595 22.8405 19.9478 22.5523L27.9167 14.5833C28.6071 13.893 28.6071 12.7737 27.9167 12.0833C27.2264 11.393 26.1071 11.393 25.4167 12.0833L17.4478 20.0523C17.1595 20.3405 17.0154 20.4847 16.9123 20.6528C16.8209 20.802 16.7536 20.9645 16.7128 21.1346C16.6667 21.3264 16.6667 21.5302 16.6667 21.9379V23.3333Z" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
        </g>
        <defs><clipPath id="clip_edit_icon"><rect width="20" height="20" fill="white" transform="translate(10 10)"/></clipPath></defs>
      </svg>
    </IconActionButton>
  );
}

export function CopyIconButton({ onClick, title = 'Copy & Edit', ariaLabel, size = 22, style }) {
  return (
    <IconActionButton onClick={onClick} title={title} ariaLabel={ariaLabel} hoverColor="#0891b2" hoverBg="#ecfeff" style={style}>
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clipPath="url(#clip_copy_icon)">
          <path d="M23.3333 13.3333H24.6667C25.3997 13.3333 25.7662 13.3333 26.0567 13.4426C26.4594 13.5916 26.7751 13.9073 26.9241 14.31C27.0333 14.6005 27.0333 14.967 27.0333 15.7V25.3C27.0333 26.033 27.0333 26.3995 26.9241 26.69C26.7751 27.0927 26.4594 27.4084 26.0567 27.5574C25.7662 27.6667 25.3997 27.6667 24.6667 27.6667H15.3333C14.6003 27.6667 14.2338 27.6667 13.9433 27.5574C13.5406 27.4084 13.2249 27.0927 13.0759 26.69C12.9667 26.3995 12.9667 26.033 12.9667 25.3V24M13.3333 21.3333H20.6667C21.3997 21.3333 21.7662 21.3333 22.0567 21.2241C22.4594 21.0751 22.7751 20.7594 22.9241 20.3567C23.0333 20.0662 23.0333 19.6997 23.0333 18.9667V12.3667C23.0333 11.6337 23.0333 11.2672 22.9241 10.9767C22.7751 10.574 22.4594 10.2583 22.0567 10.1093C21.7662 10 21.3997 10 20.6667 10H13.3333C12.6003 10 12.2338 10 11.9433 10.1093C11.5406 10.2583 11.2249 10.574 11.0759 10.9767C10.9667 11.2672 10.9667 11.6337 10.9667 12.3667V18.9667C10.9667 19.6997 10.9667 20.0662 11.0759 20.3567C11.2249 20.7594 11.5406 21.0751 11.9433 21.2241C12.2338 21.3333 12.6003 21.3333 13.3333 21.3333Z" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
        </g>
        <defs><clipPath id="clip_copy_icon"><rect width="20" height="20" fill="white" transform="translate(10 10)"/></clipPath></defs>
      </svg>
    </IconActionButton>
  );
}

// Details toggle (ⓘ). `active` marks the pinned-open state: the button keeps
// its highlight at rest and reports aria-pressed, so the open/closed state is
// visible and accessible without relying on the hover title alone.
export function InfoIconButton({ onClick, title, ariaLabel, size = 22, style, active = false }) {
  return (
    <IconActionButton
      onClick={onClick}
      title={title || (active ? 'Hide details' : 'Show details')}
      ariaLabel={ariaLabel}
      hoverColor="var(--cux-accent, #2563eb)"
      hoverBg="var(--cux-accent-light, #eff6ff)"
      restColor={active ? 'var(--cux-accent, #2563eb)' : REST_COLOR}
      restBg={active ? 'var(--cux-accent-light, #eff6ff)' : 'none'}
      pressed={active}
      style={style}
    >
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="20" cy="20" r="8.33" stroke="currentColor" strokeWidth="1.67" />
        <path d="M20 19.1667V23.75" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" />
        <circle cx="20" cy="16.25" r="1.04" fill="currentColor" />
      </svg>
    </IconActionButton>
  );
}

export function DeleteIconButton({ onClick, title = 'Delete', ariaLabel, size = 22, style, disabled }) {
  return (
    <IconActionButton onClick={onClick} title={title} ariaLabel={ariaLabel} hoverColor="#ef4444" hoverBg="#fef2f2" disabled={disabled} style={style}>
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M23.3333 15V14.3334C23.3333 13.3999 23.3333 12.9332 23.1517 12.5767C22.9919 12.2631 22.7369 12.0081 22.4233 11.8483C22.0668 11.6667 21.6001 11.6667 20.6667 11.6667H19.3333C18.3999 11.6667 17.9332 11.6667 17.5767 11.8483C17.2631 12.0081 17.0081 12.2631 16.8483 12.5767C16.6667 12.9332 16.6667 13.3999 16.6667 14.3334V15M18.3333 19.5834V23.75M21.6667 19.5834V23.75M12.5 15H27.5M25.8333 15V24.3334C25.8333 25.7335 25.8333 26.4336 25.5608 26.9683C25.3212 27.4387 24.9387 27.8212 24.4683 28.0609C23.9335 28.3334 23.2335 28.3334 21.8333 28.3334H18.1667C16.7665 28.3334 16.0665 28.3334 15.5317 28.0609C15.0613 27.8212 14.6788 27.4387 14.4392 26.9683C14.1667 26.4336 14.1667 25.7335 14.1667 24.3334V15" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </IconActionButton>
  );
}

// ── Files page (memory) actions ───────────────────────────────────────────
// Same 40×40 viewBox / 1.67 stroke family as the buttons above. Each takes
// `disabled` because a memory file is not viewable while it is still being
// converted, and an automated row cannot be deleted from here.

// Eye — open the converted Markdown.
export function ViewIconButton({ onClick, title = 'View', ariaLabel, size = 22, style, disabled }) {
  return (
    <IconActionButton onClick={onClick} title={title} ariaLabel={ariaLabel} hoverColor="var(--cux-accent, #2563eb)" hoverBg="var(--cux-accent-light, #eff6ff)" disabled={disabled} style={style}>
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12.5 20C12.5 20 15 13.75 20 13.75C25 13.75 27.5 20 27.5 20C27.5 20 25 26.25 20 26.25C15 26.25 12.5 20 12.5 20Z" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="20" cy="20" r="2.5" stroke="currentColor" strokeWidth="1.67"/>
      </svg>
    </IconActionButton>
  );
}

// Arrow down into a tray — download the original file or the Markdown.
export function DownloadIconButton({ onClick, title = 'Download', ariaLabel, size = 22, style, disabled, pressed }) {
  return (
    <IconActionButton onClick={onClick} title={title} ariaLabel={ariaLabel} hoverColor="#059669" hoverBg="#ecfdf5" disabled={disabled} pressed={pressed} style={style}>
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 12.5V22.5M20 22.5L16.25 18.75M20 22.5L23.75 18.75" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M13.3333 22.5V25C13.3333 26.3807 14.4526 27.5 15.8333 27.5H24.1667C25.5474 27.5 26.6667 26.3807 26.6667 25V22.5" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </IconActionButton>
  );
}

// Circular arrow — reprocess a failed file.
export function RetryIconButton({ onClick, title = 'Retry', ariaLabel, size = 22, style, disabled }) {
  return (
    <IconActionButton onClick={onClick} title={title} ariaLabel={ariaLabel} hoverColor="#d97706" hoverBg="#fffbeb" disabled={disabled} style={style}>
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M26.6667 19.1667A6.6667 6.6667 0 1 1 24.75 14.4167" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M26.6667 13.3333V17.5H22.5" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </IconActionButton>
  );
}
